import { z } from 'zod';
import { aisleOf } from './aisles';
import { formatQty, scaleQty, type Ingredient, type Recipe } from './recipes';
import { addDays } from './schedule';
import { ClockTime, IsoDate, Member } from './schemas';

/**
 * Essensplan (Küche 3): one main meal per day at households/{hid}/meals/{YYYY-MM-DD}. Plus
 * (preismodell 14.10.) – the server checks it on every write. A day holds a recipe of the
 * family or just a title ("Pizza bestellen"), the kids say whether they look forward to it.
 */

/** The family's usual dinner time when nobody set one (household.mealTime). */
export const MEAL_TIME = '18:30';

export const Vote = z.enum(['yes', 'no']);
export type Vote = z.infer<typeof Vote>;

export const MealInput = z.object({
  date: IsoDate,
  recipeId: z.string().min(1).max(64).nullish(),
  /** Free text without a recipe, or the recipe's title (kept so the plan reads without it). */
  title: z.string().trim().min(1).max(80),
  /** Only when this day differs from the family's time. */
  time: ClockTime.nullish(),
  /** "Wunsch von Ben", "bei Oma" */
  note: z.string().trim().max(80).nullish(),
});
export type MealInput = z.infer<typeof MealInput>;

export const Meal = z.object({
  date: IsoDate,
  recipeId: z.string().nullable(),
  title: z.string(),
  time: ClockTime.nullable(),
  note: z.string().nullable(),
  /** memberId → yes/no ("Freu mich" / "Lieber nicht"). */
  votes: z.record(z.string(), Vote).default({}),
  updatedAt: z.number(),
});
export type Meal = z.infer<typeof Meal>;

export const VoteInput = z.object({
  date: IsoDate,
  memberId: z.string().min(1).max(128),
  /** null takes the vote back. */
  vote: Vote.nullable(),
});

/** Allergies and dislikes per person (members/{id}.food) – a warning in the plan, no filter. */
export const FoodPrefs = Member.shape.food.unwrap();
export type FoodPrefs = z.infer<typeof FoodPrefs>;

export const FoodPrefsInput = FoodPrefs.extend({ memberId: z.string().min(1).max(128) });

/** "Nüsse, Erdbeeren" → ["Nüsse", "Erdbeeren"] – the way a parent types it. */
export const splitPrefs = (text: string) =>
  [
    ...new Set(
      text
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ].slice(0, 10);

const norm = (s: string) => s.toLocaleLowerCase('de').normalize('NFC');

/** Does an ingredient name hit this word? "Haselnüsse" hits "nüsse", "Erdnuss" hits "Erdnuss". */
function hits(ingredient: string, word: string) {
  const i = norm(ingredient);
  const w = norm(word);
  // "Nüsse" also catches "Haselnuss": compare the singular-ish stem too
  const stem = w.replace(/(nüsse|nusse)$/, 'nuss').replace(/(e|en|n|s)$/, '');
  return i.includes(w) || (stem.length >= 3 && i.includes(stem));
}

export interface PrefHit {
  memberId: string;
  name: string;
  word: string;
  kind: 'allergy' | 'dislike';
}

/** Who should know about this recipe: allergies first, then dislikes; one hit per person+word. */
export function prefHits(
  ingredients: Pick<Ingredient, 'name'>[],
  members: { id: string; name: string; food?: FoodPrefs }[],
): PrefHit[] {
  const out: PrefHit[] = [];
  for (const kind of ['allergy', 'dislike'] as const) {
    for (const m of members) {
      const words = kind === 'allergy' ? m.food?.allergies : m.food?.dislikes;
      for (const word of words ?? []) {
        if (ingredients.some((i) => hits(i.name, word))) {
          out.push({ memberId: m.id, name: m.name, word, kind });
        }
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// The week
// ---------------------------------------------------------------------------------------------

const toDate = (iso: string) => new Date(`${iso}T12:00:00Z`);

/** Monday of the week `iso` lies in. */
export function mondayOf(iso: string): string {
  const day = toDate(iso).getUTCDay(); // 0 = Sunday
  return addDays(iso, -((day + 6) % 7));
}

/** The seven days Monday … Sunday of the week `iso` lies in. */
export const weekOf = (iso: string) => {
  const mo = mondayOf(iso);
  return Array.from({ length: 7 }, (_, n) => addDays(mo, n));
};

export interface WeekItem {
  name: string;
  spec: string;
  aisle: ReturnType<typeof aisleOf>;
  /** Which dishes need it – shown in the sheet ("für Mo, Mi"). */
  dates: string[];
}

/**
 * "Zutaten der Woche" (Plus): what the planned recipes from `from` on need, bundled – the same
 * article with the same unit is added up, so the list gets "600 g Hackfleisch" once, not twice.
 */
export function weekIngredients(
  meals: Pick<Meal, 'date' | 'recipeId'>[],
  recipes: (Pick<Recipe, 'ingredients' | 'servings'> & { id: string })[],
  from: string,
): WeekItem[] {
  const byId = new Map(recipes.map((r) => [r.id, r]));
  const out = new Map<string, WeekItem & { qty: number | null; unit: string }>();
  for (const m of [...meals].sort((a, b) => a.date.localeCompare(b.date))) {
    if (m.date < from || !m.recipeId) continue;
    const r = byId.get(m.recipeId);
    if (!r) continue;
    for (const i of r.ingredients) {
      const key = `${norm(i.name)}|${norm(i.unit)}`;
      const qty = i.qty === null ? null : scaleQty(i.qty, r.servings, r.servings);
      const prev = out.get(key);
      if (!prev) {
        out.set(key, {
          name: i.name,
          unit: i.unit,
          qty,
          spec: '',
          aisle: aisleOf(i.name),
          dates: [m.date],
        });
        continue;
      }
      prev.qty = prev.qty === null || qty === null ? (prev.qty ?? qty) : prev.qty + qty;
      if (!prev.dates.includes(m.date)) prev.dates.push(m.date);
    }
  }
  return [...out.values()].map(({ qty, unit, ...rest }) => ({
    ...rest,
    spec: qty === null ? unit : [formatQty(qty), unit].filter(Boolean).join(' '),
  }));
}
